import {
  CaretDown, CaretUp, ChatCenteredDots,
  ChatsCircle,
} from '@phosphor-icons/react';
import {
  Avatar, Divider, Dropdown, Flex,
  message,
  Spin,
  Tooltip
} from 'antd';
import React, { useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Typography } from 'tfc-components';

import UserDropdown from '../UserDropdown';

import logo from 'assets/images/logo.svg';
import Icon from 'components/atoms/Icon';
import Image from 'components/atoms/Image';
import Loading from 'components/atoms/Loading';
import { getAgentsService } from 'services/agents';
import { AgentDataByIdType } from 'services/agents/types';
import { logoutService } from 'services/auth';
import { resetAuth } from 'store/auth';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ROUTE_PATHS } from 'utils/constants';
import mapModifiers, { getFirstLetters } from 'utils/functions';

interface HeaderProps {
  agentData?: AgentDataByIdType;
  reloadAgent?: () => void;
}

const itemsFeature = [
  {
    key: 'Chat Agent',
    label: 'Chat Agent',
    value: 'chatAgent',
    icon: <ChatsCircle className="o-sidebar_icon-active" size={20} />,
  },
  {
    key: 'Voice Agent',
    label: 'Voice Agent',
    value: 'voiceAgent',
    icon: <Icon iconName="micVoice" size="20" />,
  },
];

const Header: React.FC<HeaderProps> = ({ agentData, reloadAgent }) => {
  const [agentOpen, setAgentOpen] = useState(false);
  const [featOpen, setFeatOpen] = useState(false);
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const featParams = searchParams.get('feat');
  const [userOpen, setUserOpen] = useState(false);
  const profile = useAppSelector((state) => state.auth.profile);

  const navigate = useNavigate();

  const {
    data: listAgentData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ['agents'],
    ({ pageParam = 1 }) => getAgentsService({
      limit: 10,
      page: pageParam,
    }),
    {
      getNextPageParam: (lastPage) => {
        const { page, totalPages } = lastPage.meta;

        if (page < totalPages) {
          return page + 1;
        }

        return undefined;
      },
      onError: () => {
        message.error('Unable to load agent list. Please try again later.');
      },
    }
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (
      scrollHeight - scrollTop <= clientHeight + 20
      && hasNextPage
      && !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const agents = useMemo(
    () => listAgentData?.pages.flatMap((page) => page.data) ?? [],
    [listAgentData]
  );

  const handleLogout = async () => {
    try {
      setLoading(true);
      await logoutService();
      dispatch(resetAuth());
      navigate(ROUTE_PATHS.LOGIN);
    } catch {
      message.error('Logout failed!');
    } finally {
      setLoading(false);
    }
    setUserOpen(false);
  };

  const renderAgentDropdown = () => (
    <div className="t-header_listAgent" onScroll={handleScroll}>
      {agents.map((agent) => (
        <div
          key={agent.agentData.code}
          className={mapModifiers('t-header_listAgent_item', agent.agentData.code === key && 'active')}
          onClick={() => {
            if (agent.agentData.code === key) {
              return;
            }
            reloadAgent?.();
            setAgentOpen(false);
            window.location.replace(`/edit-agent?key=${agent.agentData.code}&feat=${featParams === 'voiceAgent' ? 'voiceAgent' : 'chatAgent'}`);
          }}
        >
          <Avatar size={28} src={agent.agentData.avatar} />
          <Typography.Text>{agent.agentData.name}</Typography.Text>
        </div>
      ))}

      {isFetchingNextPage && (
        <div className="t-header_listAgent_loading">
          <Spin size="small" />
        </div>
      )}
    </div>
  );

  const renderChatVoiceDropdown = () => (
    <div className="t-header_listAgent" onScroll={handleScroll}>
      {itemsFeature.map((feat) => (
        <div
          key={key}
          className={mapModifiers('t-header_listAgent_item', featParams === feat.value && 'active')}
          onClick={() => {
            if (featParams === feat.value) {
              return;
            }
            navigate(`/edit-agent?key=${key}${feat.value === 'voiceAgent' ? '&tab=workflow' : ''}&feat=${feat.value}`);
            setFeatOpen(false);
          }}
        >
          {feat.icon}
          <Typography.Text>{feat.label}</Typography.Text>
        </div>
      ))}

      {
        isFetchingNextPage && (
          <div className="t-header_listAgent_loading">
            <Spin size="small" />
          </div>
        )
      }
    </div>
  );

  if (isLoading) {
    return (
      <Loading variant="fullscreen" width={50} />
    );
  }

  return (
    <Flex justify="space-between" className="t-header">
      <div className="t-header_left">
        <div onClick={() => navigate(ROUTE_PATHS.AGENT)} className="t-mainLayout_logo">
          <Image src={logo} ratio="1x1" alt="logo" />
        </div>
        {agentData && (
          <Flex align="center" gap={16}>
            <Divider type="vertical" className="t-header_divider" />
            <div>
              <Dropdown
                trigger={['click']}
                placement="bottomLeft"
                open={agentOpen}
                onOpenChange={setAgentOpen}
                // eslint-disable-next-line react/no-unstable-nested-components
                dropdownRender={renderAgentDropdown}
              >
                <Flex gap={10} align="center">
                  <Avatar src={agentData?.agentData.avatar} size={32} style={{ cursor: 'pointer' }} />
                  <Typography.Text extendClasses="fs-14x22">{agentData?.agentData.name || 'Agent'}</Typography.Text>
                  {/* caret up and down */}
                  {agentOpen ? <CaretUp size={24} /> : <CaretDown size={24} />}
                </Flex>
              </Dropdown>
            </div>
            <Divider type="vertical" className="t-header_divider" />
            <div>
              <Dropdown
                trigger={['click']}
                placement="bottomLeft"
                open={featOpen}
                onOpenChange={setFeatOpen}
                // eslint-disable-next-line react/no-unstable-nested-components
                dropdownRender={renderChatVoiceDropdown}
              >
                <Flex gap={10} align="center">
                  <div className={mapModifiers('t-header_feat', featParams === 'voiceAgent' ? 'voice' : 'chat')}>
                    {featParams === 'voiceAgent' ? <Icon iconName="micVoice" size="24" /> : <ChatsCircle className="t-header_feat_icon" size={24} />}
                  </div>
                  <Typography.Text extendClasses="fs-14x22">{featParams === 'voiceAgent' ? 'Voice Agent' : 'Chat Agent'}</Typography.Text>
                  {/* caret up and down */}
                  {featOpen ? <CaretUp size={24} /> : <CaretDown size={24} />}
                </Flex>
              </Dropdown>
            </div>
          </Flex>
        )}
      </div>
      <Flex align="center" justify="center" gap={8} className="t-header_right">
        {/* <Typography.Text extendClasses="t-header_right_text fs-14x22">Feedback</Typography.Text>
        <BellSimple className="t-header_right_icon" size={24} /> */}
        <Tooltip title="Ask2Auto GPT is coming soon!"><ChatCenteredDots className="t-header_right_icon" size={24} /></Tooltip>

        <Dropdown
          trigger={['click']}
          placement="bottomRight"
          open={userOpen}
          onOpenChange={setUserOpen}
          // eslint-disable-next-line react/no-unstable-nested-components
          dropdownRender={() => (
            <UserDropdown
              onClose={() => setUserOpen(false)}
              onLogout={handleLogout}
            />
          )}
        >
          <div className="t-header_right_avatar">
            <Avatar size={32} style={{ backgroundColor: '#012B61', cursor: 'pointer' }} alt={profile?.customerData.name} src={profile?.customerData.avatar}>
              {!profile?.customerData.avatar && (
                <Typography.Text extendClasses="f-14x16">
                  {getFirstLetters(profile?.customerData.name || '')}
                </Typography.Text>
              )}
            </Avatar>
          </div>
        </Dropdown>
      </Flex>
    </Flex>
  );
};

export default Header;
